import dayjs from 'dayjs';

import { TIME_OPTIONS } from './timeUtils';

import type { TimeOption } from './timeUtils';

/** Minimum time buffer in minutes for same-day reservations. */
const MIN_TIME_BUFFER_MINUTES = 5;

/** Combine a calendar day with an `HH:mm` time string into a single Date. */
export const combineDateAndTime = (date: Date, time: string): Date => {
  const [hours, minutes] = time.split(':').map(Number);
  return dayjs(date).hour(hours).minute(minutes).second(0).millisecond(0).toDate();
};

/** Minutes-since-midnight for an `HH:mm` time string. */
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Calculate the minimum allowed time for a given date.
 * If the date is today, returns current time + MIN_TIME_BUFFER_MINUTES, rounded up to the next 10-minute interval.
 * If the date is in the future, returns 00:00 (midnight).
 */
const getMinimumAllowedTime = (date: Date): { hour: number; minute: number } => {
  const now = dayjs();

  // If the date is not today, allow any time (starting from midnight)
  if (!dayjs(date).isSame(now, 'day')) {
    return { hour: 0, minute: 0 };
  }

  // For today, calculate current time + buffer, rounded up to next 10-minute interval
  const currentMinutes = now.hour() * 60 + now.minute();
  const minimumMinutes = currentMinutes + MIN_TIME_BUFFER_MINUTES;

  // Round up to next 10-minute interval
  const roundedMinutes = Math.ceil(minimumMinutes / 10) * 10;

  // Handle overflow: if roundedMinutes >= 1440 (24 hours), cap at 23:59
  if (roundedMinutes >= 1440) {
    return { hour: 23, minute: 59 };
  }

  const hour = Math.floor(roundedMinutes / 60);
  const minute = roundedMinutes % 60;

  return { hour, minute };
};

/**
 * The `TIME_OPTIONS` that are selectable for a given day:
 *  - the whole day is before `minDate`'s day → nothing is selectable;
 *  - if the day is today, past times (and a small buffer) are removed;
 *  - if `minDate` falls on the same day, only times after it are kept
 *    (e.g. an end time on the same day as the start must be later).
 * Returns all options when no day is selected yet.
 */
export const getSelectableTimeOptions = (
  selectedDate: Date | undefined,
  minDate?: Date,
): TimeOption[] => {
  if (!selectedDate) return TIME_OPTIONS;

  // A day entirely before minDate is disallowed — no time is selectable.
  if (minDate && dayjs(selectedDate).isBefore(dayjs(minDate), 'day')) return [];

  const { hour, minute } = getMinimumAllowedTime(selectedDate);
  const minInclusive = hour * 60 + minute;
  const minExclusive =
    minDate && dayjs(selectedDate).isSame(dayjs(minDate), 'day')
      ? dayjs(minDate).hour() * 60 + dayjs(minDate).minute()
      : -1;

  return TIME_OPTIONS.filter((option) => {
    const minutes = timeToMinutes(option.value);
    return minutes >= minInclusive && minutes > minExclusive;
  });
};

/**
 * Bumps `date`'s time-of-day forward to the first still-selectable option
 * (per `getSelectableTimeOptions`) when it's no longer valid — e.g. a
 * prefilled "now" default that went stale while a form sat open. Returns
 * `date` unchanged when it's already selectable or nothing is selectable
 * left for that day.
 */
export const snapToSelectableTime = (date: Date, minDate?: Date): Date => {
  const options = getSelectableTimeOptions(date, minDate);
  if (options.length === 0) return date;
  const currentTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  if (options.some((option) => option.value === currentTime)) return date;
  return combineDateAndTime(date, options[0].value);
};

/**
 * Combine `date`'s calendar day with `preferredTime`, snapping to the earliest
 * remaining slot when that time is not selectable (e.g. 12:00 on today after noon).
 * Returns undefined when the day has no remaining slots.
 */
export const combineDateWithSelectableTime = (
  date: Date,
  preferredTime: string,
  minDate?: Date,
): Date | undefined => {
  const selectable = getSelectableTimeOptions(date, minDate);
  if (selectable.length === 0) return undefined;
  const time = selectable.some((option) => option.value === preferredTime)
    ? preferredTime
    : selectable[0].value;
  return combineDateAndTime(date, time);
};

/**
 * Earliest valid datetime at/after `from`'s day (default: today), walking forward
 * when that day has no remaining slots (e.g. late at night). Respects `minDate`.
 */
export const getEarliestSelectableDateTime = (minDate?: Date, from?: Date): Date => {
  let candidate = dayjs(from ?? undefined).startOf('day');
  const today = dayjs().startOf('day');
  if (candidate.isBefore(today, 'day')) candidate = today;
  if (minDate && candidate.isBefore(dayjs(minDate), 'day')) {
    candidate = dayjs(minDate).startOf('day');
  }
  for (let i = 0; i < 14; i += 1) {
    const selectable = getSelectableTimeOptions(candidate.toDate(), minDate);
    if (selectable.length > 0) {
      return combineDateAndTime(candidate.toDate(), selectable[0].value);
    }
    candidate = candidate.add(1, 'day');
  }
  return combineDateAndTime(candidate.toDate(), '12:00');
};
